import type { DateTime } from "luxon";
import { isConstantBonusSnapshot } from "../../../../utils/incentive";
import { type BaseRequest, NotificationType, type OpenShiftRequest } from "../../../../utils/models";
import { CallOffBypassWindow } from "../../../../utils/utilFunctions";

/** The posted-shift sheet's steps. Stable strings: they are the analytics `step` values and the sheet's test-id keys. */
export enum GapsPostedStep {
	Actions = "actions",
	Incentive = "incentive",
	Hours = "hours",
	Needed = "needed",
}

// The posted-shift sheet's rules (P3-10), each the rule an existing surface or the backend applies, named once so the
// sheet and its tests read the same thing.

/**
 * Whether the sheet may edit a request's incentive: only a CONSTANT one — no incentive, or one window from the start
 * with no end. That is exactly what the backend accepts (`services/request.py` refuses anything else with
 * `incentive.bonus.cannot.edit.decay`) and what the Requests screen's own menu offers (`canAddOrEditFlatBonus`). A decay
 * curve fails it, and so does a single window that starts later or ends: it changes over time too.
 */
export const isIncentiveEditable = (request: Pick<BaseRequest, "incentive_windows">): boolean => isConstantBonusSnapshot(request.incentive_windows);

/**
 * The hours edit's rule for a request somebody has answered: its window may only SHRINK (`useUpdateRequestTime` refuses
 * a wider one). `true` when the draft reaches before the posted start or after the posted end.
 */
export const widensAnsweredWindow = (posted: { start: DateTime; end: DateTime }, draft: { start: DateTime; end: DateTime }): boolean =>
	draft.start < posted.start || draft.end > posted.end;

/** The robo-call row: what it says, and whether a tap starts a call. */
export type RoboCallRowCopy = { labelKey: string; descriptionKey: string; isActionable: boolean };

/**
 * The robo-call row for where the shift stands against the window `InitiateRoboCallButton` gates on
 * (`callOffBypassWindowOf`). A shift already past the window will never reach it, so it is told the call is no longer
 * available rather than "available from Nh before the shift starts".
 */
export const roboCallRowCopy = (isRunning: boolean, window: CallOffBypassWindow): RoboCallRowCopy => {
	if (isRunning) return { labelKey: "gaps.postedSheet.roboCallRunning", descriptionKey: "gaps.postedSheet.roboCallRunningHint", isActionable: false };
	switch (window) {
		case CallOffBypassWindow.Open:
			return { labelKey: "gaps.postedSheet.roboCall", descriptionKey: "gaps.postedSheet.roboCallHint", isActionable: true };
		case CallOffBypassWindow.NotYet:
			return { labelKey: "gaps.postedSheet.roboCall", descriptionKey: "gaps.postedSheet.roboCallWindow", isActionable: false };
		case CallOffBypassWindow.Closed:
			return { labelKey: "gaps.postedSheet.roboCall", descriptionKey: "gaps.postedSheet.roboCallClosed", isActionable: false };
	}
};

/** The send-to-agency row, or `null` when the sheet draws none (the Requests screen's own split). */
export type AgencyRowCopy = { isSent: boolean; descriptionKey: string; isActionable: boolean };

/**
 * The send-to-agency row: only for an OPEN SHIFT in a department with an agency configured (the classic card draws
 * the same one), then what state the post is in — sent, scheduled to go, can go now, or cannot (no sub-role for the
 * job, already filled).
 */
export const agencyRowCopy = (
	request: Pick<BaseRequest, "request_type"> & Partial<Pick<OpenShiftRequest, "agency_post_delay_hours">>,
	agency: { configuredAgencyCount: number; isPostedToAgency: boolean; canNewlyPostToAgency: boolean },
): AgencyRowCopy | null => {
	if (request.request_type !== NotificationType.OpenShift || agency.configuredAgencyCount === 0) return null;
	if (agency.isPostedToAgency) return { isSent: true, descriptionKey: "gaps.postedSheet.agencySentHint", isActionable: false };
	if (request.agency_post_delay_hours != null) return { isSent: false, descriptionKey: "gaps.postedSheet.agencyScheduled", isActionable: false };
	return agency.canNewlyPostToAgency
		? { isSent: false, descriptionKey: "gaps.postedSheet.agencyHint", isActionable: true }
		: { isSent: false, descriptionKey: "gaps.postedSheet.agencyUnavailable", isActionable: false };
};

/** The delete confirm's copy — the Requests screen's own (`useRequestItemActions`) for each request type. */
export const deleteConfirmCopy = (request: Pick<BaseRequest, "request_type">): { titleKey: string; bodyKey: string } =>
	request.request_type === NotificationType.ReplaceShift
		? { titleKey: "are.you.sure", bodyKey: "delete.replace.warning" }
		: { titleKey: "delete.open.shift.title", bodyKey: "delete.open.shift.warning" };
